<script setup lang="ts">
/**
 * 源站两套履带都是 140px 槽：idle = JackpotScrollerMove + sv(speed:1)；
 * spin/parked = JackpotScroller（speed:20，停在金框正中）。
 * 本地槽宽随 --jp 缩放，位移按 140px 源槽换算，格速与源站一致。
 */
import { nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import {
  JACKPOT_REEL_IDLE_COPIES,
  JACKPOT_REEL_IDLE_SPEED,
  JACKPOT_REEL_SPIN_COPIES,
  JACKPOT_REEL_SPIN_SPEED,
  JACKPOT_REEL_VISIBLE_COUNT,
  jackpotReelFrameDelta,
  jackpotReelIdleWrap,
  jackpotReelSpinStartOffset,
  jackpotReelSpinWrap,
  jackpotReelStopOffset,
  type JackpotReelBall,
  type JackpotReelTarget,
} from '@/shared/theme/oro-mx-jackpot-reel';
import { jackpotReelLetterSrc, oroMxJackpotAssets } from '@/shared/theme/oro-mx-jackpot-assets';

const props = defineProps<{
  balls: JackpotReelBall[];
}>();

const emit = defineEmits<{
  finish: [];
}>();

type ReelState = 'idle' | 'spin' | 'parked';

const trackRef = ref<HTMLElement | null>(null);
const renderList = ref<JackpotReelBall[]>([]);
const state = ref<ReelState>('idle');
let offset = 0;
let ballSize = 0;
let animId = 0;
let spinArmed = false;

function copyCount(): number {
  return state.value === 'idle' ? JACKPOT_REEL_IDLE_COPIES : JACKPOT_REEL_SPIN_COPIES;
}

function rebuildList() {
  const copies = copyCount();
  renderList.value = props.balls.length
    ? Array.from({ length: copies }, () => props.balls).flat()
    : [];
}

function measureBallSize() {
  const first = trackRef.value?.querySelector('.ball') as HTMLElement | null;
  const width = first?.offsetWidth ?? 0;
  if (width > 0) ballSize = width;
}

function copyWidth(): number {
  if (ballSize <= 0 || props.balls.length === 0) return 0;
  return props.balls.length * ballSize;
}

function applyTransform() {
  const track = trackRef.value;
  if (!track) return;
  track.style.transform = `translate3d(${offset}px,0,0)`;
}

function loop() {
  if (state.value === 'idle') {
    measureBallSize();
    const width = copyWidth();
    if (width > 0) {
      offset -= jackpotReelFrameDelta(ballSize, JACKPOT_REEL_IDLE_SPEED);
      offset = jackpotReelIdleWrap(offset, width);
    }
  } else if (state.value === 'spin') {
    if (!spinArmed) {
      measureBallSize();
      if (ballSize > 0) {
        offset = jackpotReelSpinStartOffset(ballSize, JACKPOT_REEL_VISIBLE_COUNT);
        spinArmed = true;
      }
    } else {
      offset -= jackpotReelFrameDelta(ballSize, JACKPOT_REEL_SPIN_SPEED);
      offset = jackpotReelSpinWrap(offset, copyWidth());
    }
  }
  applyTransform();
  animId = window.requestAnimationFrame(loop);
}

function resetIdle() {
  spinArmed = false;
  ballSize = 0;
  state.value = 'idle';
  rebuildList();
  void nextTick(() => {
    if (state.value !== 'idle') return;
    measureBallSize();
    offset = 0;
    applyTransform();
  });
}

function start() {
  spinArmed = false;
  ballSize = 0;
  state.value = 'spin';
  rebuildList();
  void nextTick(() => {
    if (state.value !== 'spin') return;
    measureBallSize();
    if (ballSize <= 0) return;
    offset = jackpotReelSpinStartOffset(ballSize, JACKPOT_REEL_VISIBLE_COUNT);
    applyTransform();
    spinArmed = true;
  });
}

function stop(target: JackpotReelTarget) {
  measureBallSize();
  const next = jackpotReelStopOffset(
    props.balls,
    target,
    ballSize,
    JACKPOT_REEL_VISIBLE_COUNT,
  );
  spinArmed = false;
  state.value = 'parked';
  if (next !== null) offset = next;
  applyTransform();
  emit('finish');
}

function cancel() {
  resetIdle();
}

watch(
  () => props.balls,
  () => {
    if (state.value === 'spin') return;
    resetIdle();
  },
  { immediate: true },
);

onMounted(() => {
  measureBallSize();
  animId = window.requestAnimationFrame(loop);
});

onBeforeUnmount(() => {
  window.cancelAnimationFrame(animId);
});

defineExpose({ cancel, start, stop });
</script>

<template>
  <div class="jackpot" :class="{ 'is-draw': state !== 'idle' }" :data-reel-mode="state">
    <div class="jackpot-mask">
      <div ref="trackRef" class="track">
        <div
          v-for="(ball, index) in renderList"
          :key="`${ball.poolKey}-${ball.index}-${index}`"
          class="ball"
          :class="`type-${ball.poolKey.toLowerCase()}`"
        >
          <div class="image">
            <img :src="jackpotReelLetterSrc(ball.poolKey, ball.letter)" alt="" />
          </div>
        </div>
      </div>
    </div>
    <div class="center-marker">
      <div class="img">
        <img :src="oroMxJackpotAssets.spin" alt="" />
      </div>
    </div>
  </div>
</template>

<style scoped>
.jackpot {
  position: relative;
  z-index: 1;
  width: 100%;
  height: 100%;
}

.jackpot-mask {
  position: absolute;
  top: calc(0.1 * var(--jp, 1rem));
  left: calc(0.14 * var(--jp, 1rem));
  width: 95.8%;
  height: calc(1.16 * var(--jp, 1rem));
  overflow: hidden;
  direction: ltr;
}

.track {
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  width: max-content;
  height: 100%;
  will-change: transform;
}

.ball {
  display: flex;
  flex: 0 0 calc(1.4 * var(--jp, 1rem));
  align-items: center;
  justify-content: center;
  width: calc(1.4 * var(--jp, 1rem));
  height: 100%;
}

.image img {
  display: block;
  width: calc(0.76 * var(--jp, 1rem));
  height: auto;
}

.center-marker {
  position: absolute;
  top: 50%;
  left: 50%;
  z-index: 2;
  pointer-events: none;
  transform: translate(-50%, -50%);
}

.center-marker img {
  display: block;
  width: calc(1.26 * var(--jp, 1rem));
  height: auto;
}
</style>
